import { useEffect, useMemo, useState } from 'react';

import { motion } from 'framer-motion';
import { t } from 'i18next';
import Lottie from 'lottie-react';

import DiaryBookCover from './DiaryBookCover';
import bookAnimation from '../../../lotties/bookAnimation.json';
import { getDiaryCoverPreset } from '../../../utils/photoCardDiaryCoverStorage';
import '../MyPhotoCardDiaryDetailPage.css';

const PAGE_SLOT_COUNT = 4;
const SPREAD_SLOT_COUNT = 8;
const preloadedDiaryImageSet = new Set();

const bookIntroDelay = 0.18;
const bookIntroDuration = 0.96;
const mobileViewportEntryOffsetX = -130;
const mobileViewportEntryOffsetY = 24;
const mobileViewportRotationDelay = 1;
const mobileViewportRotationDuration = 0.5;
const mobileViewportAutoOpenDelay = 1.66;
const pageTurnDuration = 0.72;
const autoFlipDelayMs = 1200;
const autoFlipFastDelayMs = 600;
const backCoverLogoSrc = `${process.env.PUBLIC_URL}/assets/logos/fanhearts_logo.svg`;
const preloadAnimationStyle = {
  width: '100%',
  height: '100%'
};

const forwardSheetVariants = {
  idle: { rotateY: 0 },
  turning: { rotateY: -180 }
};

const backwardSheetVariants = {
  idle: { rotateY: 0 },
  turning: { rotateY: 180 }
};

export const getPhotoCardImageUrl = (photoCard) =>
  photoCard.mediumThumbnailUrl || photoCard.thumbnailUrl || photoCard.pictureUrl || '';

const buildPlaceholder = (slotId) => ({ slotId, isPlaceholder: true });

const fillPageSlots = (photoCards, spreadIndex, pageKey) => {
  const pageSlots = [...photoCards];
  while (pageSlots.length < PAGE_SLOT_COUNT) {
    pageSlots.push(buildPlaceholder(`${pageKey}-${spreadIndex}-${pageSlots.length}`));
  }
  return pageSlots;
};

const buildSlotsPage = (photoCards, spreadIndex, pageKey) => ({
  pageId: `${pageKey}-${spreadIndex}`,
  pageType: 'slots',
  slots: fillPageSlots(photoCards, spreadIndex, pageKey)
});

const buildBlankPaperPage = (pageId) => ({ pageId, pageType: 'blank-paper', slots: [] });

const buildTransparentCoverPage = (pageId, engraving = {}) => ({
  pageId,
  pageType: 'transparent-cover',
  slots: [],
  engravingEyebrow: engraving.eyebrow || '',
  engravingTitle: engraving.title || ''
});

export const buildSpreadPages = (photoCards, closingEngraving) => {
  const spreads = [];
  const totalSpreads = Math.max(1, Math.ceil(photoCards.length / SPREAD_SLOT_COUNT));
  const lastSpreadCardCount =
    photoCards.length === 0 ? 0 : photoCards.length - (totalSpreads - 1) * SPREAD_SLOT_COUNT;
  const shouldUseBackCoverOnLastSpread = lastSpreadCardCount <= PAGE_SLOT_COUNT;

  for (let spreadIndex = 0; spreadIndex < totalSpreads; spreadIndex += 1) {
    const startIndex = spreadIndex * SPREAD_SLOT_COUNT;
    const spreadCards = photoCards.slice(startIndex, startIndex + SPREAD_SLOT_COUNT);
    const isLastSpread = spreadIndex === totalSpreads - 1;
    const rightPage =
      isLastSpread && shouldUseBackCoverOnLastSpread
        ? buildTransparentCoverPage(`closing-right-${spreadIndex}`, closingEngraving)
        : buildSlotsPage(spreadCards.slice(PAGE_SLOT_COUNT), spreadIndex, 'right');

    spreads.push({
      spreadId: `content-${spreadIndex}`,
      left: buildSlotsPage(spreadCards.slice(0, PAGE_SLOT_COUNT), spreadIndex, 'left'),
      right: rightPage
    });
  }

  if (!shouldUseBackCoverOnLastSpread) {
    spreads.push({
      spreadId: `closing-${totalSpreads}`,
      left: buildBlankPaperPage(`closing-left-${totalSpreads}`),
      right: buildTransparentCoverPage(`closing-right-${totalSpreads}`, closingEngraving)
    });
  }

  return spreads;
};

function DiarySlot({ photoCard }) {
  if (photoCard.isPlaceholder) {
    return <div className="diary-slot diary-slot--placeholder" aria-hidden="true" />;
  }

  return (
    <div className="diary-slot">
      <img
        className="diary-slot-image"
        src={getPhotoCardImageUrl(photoCard)}
        alt={photoCard.event?.name || photoCard.eventName || photoCard.photoCardId}
        loading="eager"
        decoding="async"
      />
      <div className="diary-slot-overlay" aria-hidden="true" />
      {photoCard.collectedCount > 1 && (
        <div className="diary-slot-count">x{photoCard.collectedCount}</div>
      )}
    </div>
  );
}

function DiaryBackCoverMarkings({ eyebrow = '', title = '' }) {
  return (
    <>
      <img className="diary-back-cover-logo" src={backCoverLogoSrc} alt="" aria-hidden="true" />
      <div className="diary-back-cover-engraving" aria-hidden="true">
        {eyebrow ? <div className="diary-back-cover-engraving-eyebrow">{eyebrow}</div> : null}
        {title ? <div className="diary-back-cover-engraving-title">{title}</div> : null}
      </div>
    </>
  );
}

function DiaryPagePaper({ page, side, as: Component = 'section', className = '', ...props }) {
  const normalizedPage = Array.isArray(page)
    ? { pageId: `${side}-legacy`, pageType: 'slots', slots: page }
    : page;
  const pageType = normalizedPage?.pageType || 'slots';
  const pageSlots = normalizedPage?.slots || [];
  const engravingEyebrow = normalizedPage?.engravingEyebrow || '';
  const engravingTitle = normalizedPage?.engravingTitle || '';

  return (
    <Component
      className={`diary-page-surface diary-page-surface--${side} diary-page-surface--${pageType} ${className}`.trim()}
      {...props}
    >
      {pageType === 'slots'
        ? <div className="diary-page-grid">
            {pageSlots.map((photoCard) => (
              <DiarySlot key={photoCard.slotId || photoCard.photoCardId} photoCard={photoCard} />
            ))}
          </div>
        : null}
      {pageType === 'transparent-cover'
        ? <DiaryBackCoverMarkings eyebrow={engravingEyebrow} title={engravingTitle} />
        : null}
    </Component>
  );
}

function DiaryTurningSheet({ direction, frontPage, backPage, onTurnComplete }) {
  const isForward = direction === 'forward';
  const variants = isForward ? forwardSheetVariants : backwardSheetVariants;

  return (
    <motion.div
      className={`diary-turn-sheet diary-turn-sheet--${direction}`}
      variants={variants}
      initial="idle"
      animate="turning"
      transition={{ duration: pageTurnDuration, ease: [0.22, 0.61, 0.36, 1] }}
      onAnimationComplete={onTurnComplete}
    >
      <div className="diary-turn-sheet-face diary-turn-sheet-face--front">
        <DiaryPagePaper
          page={frontPage}
          side={isForward ? 'right' : 'left'}
          className="diary-page-surface--sheet"
        />
      </div>

      <div className="diary-turn-sheet-face diary-turn-sheet-face--back">
        <DiaryPagePaper
          page={backPage}
          side={isForward ? 'left' : 'right'}
          className="diary-page-surface--sheet"
        />
      </div>
    </motion.div>
  );
}

function DiaryBookSpread({
  leftPage,
  rightPage,
  turningSheet,
  className = '',
  as: Component = 'div',
  ...props
}) {
  const isClosingSpread = rightPage?.pageType === 'transparent-cover';

  return (
    <Component
      className={`diary-book-shell ${isClosingSpread ? 'diary-book-shell--closing' : ''} ${className}`.trim()}
      {...props}
    >
      <div
        className={`diary-book-pages ${isClosingSpread ? 'diary-book-pages--closing' : ''}`.trim()}
      >
        <div className="diary-book-spine" />
        <DiaryPagePaper page={leftPage} side="left" />
        <DiaryPagePaper page={rightPage} side="right" />
        {turningSheet
          ? <DiaryTurningSheet
              direction={turningSheet.direction}
              frontPage={turningSheet.frontPage}
              backPage={turningSheet.backPage}
              onTurnComplete={turningSheet.onTurnComplete}
            />
          : null}
      </div>
    </Component>
  );
}

export default function DiaryDetailView({
  collectedPhotoCards,
  cover,
  memberName,
  coverSubtitle,
  collectorName,
  closingEngraving,
  isLoading,
  onBack,
  shareButton
}) {
  const [spreadIndex, setSpreadIndex] = useState(0);
  const [flipDirection, setFlipDirection] = useState(1);
  const [pendingSpreadIndex, setPendingSpreadIndex] = useState(null);
  const [bookIntroState, setBookIntroState] = useState('cover');
  const [shouldAutoOpenCover, setShouldAutoOpenCover] = useState(true);
  const [autoFlipMode, setAutoFlipMode] = useState('off');
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  const [preloadState, setPreloadState] = useState({
    loadedCount: 0,
    totalCount: 0,
    isReady: false
  });

  const currentPreset = getDiaryCoverPreset(cover?.colorId);

  const spreads = useMemo(() => {
    return buildSpreadPages(collectedPhotoCards, closingEngraving);
  }, [closingEngraving, collectedPhotoCards]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (event) => setIsMobileViewport(event.matches);

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    setSpreadIndex(0);
    setFlipDirection(1);
    setPendingSpreadIndex(null);
    setBookIntroState('cover');
    setShouldAutoOpenCover(true);
    setAutoFlipMode('off');
    setPreloadState({ loadedCount: 0, totalCount: 0, isReady: false });
  }, [memberName]);

  useEffect(() => {
    if (isLoading) {
      setPreloadState({ loadedCount: 0, totalCount: 0, isReady: false });
      return;
    }

    const imageUrls = [
      ...new Set(collectedPhotoCards.map(getPhotoCardImageUrl).filter(Boolean))
    ];

    if (imageUrls.length === 0) {
      setPreloadState({ loadedCount: 0, totalCount: 0, isReady: true });
      return;
    }

    const cachedCount = imageUrls.filter((url) => preloadedDiaryImageSet.has(url)).length;
    const pendingUrls = imageUrls.filter((url) => !preloadedDiaryImageSet.has(url));

    if (pendingUrls.length === 0) {
      setPreloadState({ loadedCount: imageUrls.length, totalCount: imageUrls.length, isReady: true });
      return;
    }

    let isCancelled = false;
    let resolvedCount = cachedCount;

    setPreloadState({ loadedCount: cachedCount, totalCount: imageUrls.length, isReady: false });

    const handleResolve = (imageUrl) => {
      if (isCancelled) return;
      preloadedDiaryImageSet.add(imageUrl);
      resolvedCount += 1;
      setPreloadState({
        loadedCount: resolvedCount,
        totalCount: imageUrls.length,
        isReady: resolvedCount >= imageUrls.length
      });
    };

    pendingUrls.forEach((imageUrl) => {
      const preloadImage = new Image();
      preloadImage.onload = () => handleResolve(imageUrl);
      preloadImage.onerror = () => handleResolve(imageUrl);
      preloadImage.src = imageUrl;
    });

    return () => {
      isCancelled = true;
    };
  }, [collectedPhotoCards, isLoading, memberName]);

  const currentSpread = spreads[spreadIndex] || spreads[0];
  const targetSpread =
    pendingSpreadIndex !== null ? spreads[pendingSpreadIndex] || currentSpread : currentSpread;
  const isFlipping = pendingSpreadIndex !== null;
  const isPreparingDiary = isLoading || !preloadState.isReady;
  const isBookOpen = bookIntroState === 'open';
  const isBookCoverVisible = bookIntroState === 'cover';
  const isBookOpening = bookIntroState === 'opening';
  const isAutoFlipping = autoFlipMode !== 'off';
  const autoFlipDelay = autoFlipMode === 'fast' ? autoFlipFastDelayMs : autoFlipDelayMs;
  const coverAutoOpenDelay = isMobileViewport ? mobileViewportAutoOpenDelay : bookIntroDelay;
  const preloadProgressPercent = isLoading
    ? 12
    : preloadState.totalCount > 0
      ? Math.round((preloadState.loadedCount / preloadState.totalCount) * 100)
      : 100;
  const preloadDescription = isLoading
    ? t('myPhotoCardDiary.loadingCards', { defaultValue: '포카 목록을 정리하고 있어요.' })
    : t('myPhotoCardDiary.loadingImages', {
        defaultValue: '페이지 넘김이 자연스럽도록 이미지를 미리 불러오고 있어요.'
      });
  const shouldShowMobileCoverPose =
    isMobileViewport && bookIntroState === 'cover' && !shouldAutoOpenCover;
  const mobileViewportTarget = shouldShowMobileCoverPose
    ? { rotate: 0, x: mobileViewportEntryOffsetX, y: mobileViewportEntryOffsetY }
    : { rotate: isMobileViewport ? 90 : 0, x: 0, y: 0 };
  const mobileViewportTransitionDelay =
    isMobileViewport && bookIntroState === 'cover' && shouldAutoOpenCover
      ? mobileViewportRotationDelay
      : 0;

  useEffect(() => {
    if (isPreparingDiary || bookIntroState !== 'cover' || !shouldAutoOpenCover) return;

    const openTimer = window.setTimeout(() => {
      setBookIntroState('opening');
    }, coverAutoOpenDelay * 1000);

    return () => window.clearTimeout(openTimer);
  }, [bookIntroState, coverAutoOpenDelay, isPreparingDiary, shouldAutoOpenCover]);

  useEffect(() => {
    if (!isAutoFlipping || isPreparingDiary || isFlipping || isBookOpening) return;

    if (!isBookOpen) {
      if (!isBookCoverVisible) return;

      const openTimer = window.setTimeout(() => {
        setBookIntroState('opening');
        setShouldAutoOpenCover(false);
      }, 720);

      return () => window.clearTimeout(openTimer);
    }

    if (spreadIndex >= spreads.length - 1) {
      setAutoFlipMode('off');
      return;
    }

    const flipTimer = window.setTimeout(() => {
      setFlipDirection(1);
      setPendingSpreadIndex(spreadIndex + 1);
    }, autoFlipDelay);

    return () => window.clearTimeout(flipTimer);
  }, [
    autoFlipDelay,
    isAutoFlipping,
    isPreparingDiary,
    isFlipping,
    isBookOpening,
    isBookOpen,
    isBookCoverVisible,
    spreadIndex,
    spreads.length
  ]);

  const completeFlip = () => {
    if (pendingSpreadIndex === null) return;
    setSpreadIndex(pendingSpreadIndex);
    setPendingSpreadIndex(null);
  };

  let bookSpreadView;
  if (!isFlipping) {
    bookSpreadView = { leftPage: currentSpread.left, rightPage: currentSpread.right, turningSheet: null };
  } else if (flipDirection > 0) {
    bookSpreadView = {
      leftPage: currentSpread.left,
      rightPage: targetSpread.right,
      turningSheet: {
        direction: 'forward',
        frontPage: currentSpread.right,
        backPage: targetSpread.left,
        onTurnComplete: completeFlip
      }
    };
  } else {
    bookSpreadView = {
      leftPage: targetSpread.left,
      rightPage: currentSpread.right,
      turningSheet: {
        direction: 'backward',
        frontPage: currentSpread.left,
        backPage: targetSpread.right,
        onTurnComplete: completeFlip
      }
    };
  }

  const goPrev = () => {
    if (isPreparingDiary || isFlipping || isBookOpening) return;
    setAutoFlipMode('off');

    if (spreadIndex <= 0) {
      if (!isBookOpen) return;
      setBookIntroState('cover');
      setShouldAutoOpenCover(false);
      return;
    }

    setFlipDirection(-1);
    setPendingSpreadIndex(spreadIndex - 1);
  };

  const goNext = () => {
    if (isPreparingDiary || isFlipping || isBookOpening) return;
    setAutoFlipMode('off');

    if (!isBookOpen) {
      if (!isBookCoverVisible) return;
      setBookIntroState('opening');
      setShouldAutoOpenCover(false);
      return;
    }

    if (spreadIndex >= spreads.length - 1) return;

    setFlipDirection(1);
    setPendingSpreadIndex(spreadIndex + 1);
  };

  const canGoPrev = !isPreparingDiary && !isFlipping && !isBookOpening && isBookOpen;
  const canGoNext =
    !isPreparingDiary &&
    !isFlipping &&
    !isBookOpening &&
    (isBookCoverVisible || (isBookOpen && spreadIndex < spreads.length - 1));
  const isLastSpreadOpen = isBookOpen && spreadIndex >= spreads.length - 1;
  const isRestartAvailable = !isPreparingDiary && !isFlipping && !isBookOpening && isLastSpreadOpen;
  const canUseAutoFlipButton = !isPreparingDiary && !isBookOpening && !isFlipping;
  const autoFlipButtonAriaLabel = isRestartAvailable
    ? t('myPhotoCardDiary.autoFlipRestart', { defaultValue: '처음으로 돌아가기' })
    : autoFlipMode === 'off'
      ? t('myPhotoCardDiary.autoFlipStart', { defaultValue: '자동 넘기기' })
      : autoFlipMode === 'normal'
        ? t('myPhotoCardDiary.autoFlip2x', { defaultValue: '자동 넘기기 2배속' })
        : t('myPhotoCardDiary.autoFlipStop', { defaultValue: '자동 넘기기 중지' });

  return (
    <main
      className="diary-detail-root bg-background"
      style={{
        '--diary-cover-color': currentPreset.coverColor,
        '--diary-cover-text': currentPreset.textColor,
        '--diary-page-color': currentPreset.pageColor,
        '--diary-page-edge-color': currentPreset.pageEdgeColor,
        '--diary-accent-color': currentPreset.accentColor
      }}
    >
      <button
        type="button"
        className="diary-list-button"
        aria-label={t('myPhotoCardDiary.backToList', { defaultValue: '다이어리 목록' })}
        onClick={onBack}
      >
        <span aria-hidden="true" className="diary-list-button-icon">
          ×
        </span>
      </button>

      {!isPreparingDiary
        ? <button
            type="button"
            className="diary-auto-flip-button"
            aria-label={autoFlipButtonAriaLabel}
            aria-pressed={isRestartAvailable ? false : isAutoFlipping}
            disabled={!canUseAutoFlipButton}
            onClick={() => {
              if (isRestartAvailable) {
                setAutoFlipMode('off');
                setPendingSpreadIndex(null);
                setSpreadIndex(0);
                setFlipDirection(1);
                setBookIntroState('cover');
                setShouldAutoOpenCover(false);
                return;
              }

              setAutoFlipMode((currentMode) => {
                if (currentMode === 'off') return 'normal';
                if (currentMode === 'normal') return 'fast';
                return 'off';
              });
            }}
          >
            <span
              aria-hidden="true"
              className={`diary-auto-flip-button-icon ${
                isRestartAvailable
                  ? 'diary-auto-flip-button-icon--restart'
                  : autoFlipMode === 'off'
                    ? 'diary-auto-flip-button-icon--play'
                    : autoFlipMode === 'normal'
                      ? 'diary-auto-flip-button-icon--speed'
                      : 'diary-auto-flip-button-icon--pause'
              }`.trim()}
            >
              {isRestartAvailable ? '↻' : autoFlipMode === 'normal' ? 'x2' : null}
            </span>
          </button>
        : null}

      {shareButton ?? null}

      <div className="diary-book-stage">
        <button
          type="button"
          className={`diary-book-stage-arrow ${!isBookOpen ? 'diary-book-stage-arrow--hidden' : ''}`.trim()}
          onClick={goPrev}
          disabled={!canGoPrev}
          aria-label={t('myPhotoCardDiary.prevSpread', { defaultValue: '이전 장' })}
        >
          <span aria-hidden="true" className="diary-book-stage-arrow-icon">
            ‹
          </span>
        </button>

        {isPreparingDiary
          ? <div className="diary-preload-shell" aria-live="polite">
              <div className="diary-preload-animation" aria-hidden="true">
                <Lottie animationData={bookAnimation} loop={true} style={preloadAnimationStyle} />
              </div>
              <div className="diary-preload-description">{preloadDescription}</div>
              <div className="diary-preload-track" aria-hidden="true">
                <div
                  className="diary-preload-bar"
                  style={{ width: `${Math.min(preloadProgressPercent, 100)}%` }}
                />
              </div>
            </div>
          : <div className="diary-book-viewport">
              <div className="diary-book-viewport-inner">
                <motion.div
                  key={`diary-book-viewport-rotator-${memberName}-${isMobileViewport ? 'mobile' : 'desktop'}`}
                  className="diary-book-viewport-rotator"
                  initial={
                    isMobileViewport
                      ? { rotate: 0, x: mobileViewportEntryOffsetX, y: mobileViewportEntryOffsetY }
                      : { rotate: 0, x: 0, y: 0 }
                  }
                  animate={mobileViewportTarget}
                  transition={{
                    delay: mobileViewportTransitionDelay,
                    duration: isMobileViewport ? mobileViewportRotationDuration : 0,
                    ease: [0.22, 0.61, 0.36, 1]
                  }}
                >
                  {bookIntroState !== 'open'
                    ? <div className="diary-book-intro-stage">
                        <motion.div
                          className="diary-book-reveal-layer"
                          initial={false}
                          animate={bookIntroState === 'opening' ? { opacity: 1 } : { opacity: 0 }}
                          transition={{
                            duration: 0.62,
                            ease: [0.22, 0.61, 0.36, 1],
                            delay: bookIntroState === 'opening' ? 0.14 : 0
                          }}
                        >
                          <DiaryBookSpread
                            leftPage={currentSpread.left}
                            rightPage={currentSpread.right}
                            turningSheet={null}
                          />
                        </motion.div>

                        <div className="diary-book-cover-frame">
                          <motion.div
                            className="diary-book-cover-shell"
                            initial={false}
                            animate={
                              bookIntroState === 'opening'
                                ? { rotateY: -132, x: -18, opacity: 0.1, scale: 1 }
                                : { rotateY: 0, x: 0, opacity: 1, scale: 1 }
                            }
                            transition={{
                              duration: bookIntroDuration,
                              ease: [0.22, 0.61, 0.36, 1]
                            }}
                            onAnimationComplete={() => {
                              setBookIntroState((currentState) =>
                                currentState === 'opening' ? 'open' : currentState
                              );
                              setShouldAutoOpenCover(false);
                            }}
                          >
                            <DiaryBookCover
                              cover={cover}
                              memberName={memberName}
                              subtitle={coverSubtitle}
                              badgeText={collectorName}
                            />
                          </motion.div>
                        </div>
                      </div>
                    : <DiaryBookSpread
                        leftPage={bookSpreadView.leftPage}
                        rightPage={bookSpreadView.rightPage}
                        turningSheet={bookSpreadView.turningSheet}
                      />}
                </motion.div>
              </div>
            </div>}

        <button
          type="button"
          className={`diary-book-stage-arrow ${isLastSpreadOpen ? 'diary-book-stage-arrow--hidden' : ''}`.trim()}
          onClick={goNext}
          disabled={!canGoNext}
          aria-label={t('myPhotoCardDiary.nextSpread', { defaultValue: '다음 장' })}
        >
          <span aria-hidden="true" className="diary-book-stage-arrow-icon">
            ›
          </span>
        </button>
      </div>
    </main>
  );
}
